import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { TextInput } from "@circleco/compass/components/TextInput";
import { useMemo } from "react";
import type { ChangeEvent, FocusEvent } from "react";
import type { FieldError } from "react-hook-form";
import { get, useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { Select } from "@/react/components/SettingsApp/CompassCompat/Select";
import { filterTaxIdTypeOptionBySearchValue } from "@/react/utils/paywalls/taxId/helpers";
import type { UseTaxIdFormConfig } from "@/react/utils/paywalls/taxId/useTaxIdForm";
import { useTaxIdForm } from "@/react/utils/paywalls/taxId/useTaxIdForm";

import { CheckoutForm } from "../";
import type { CheckoutFormValuesProps } from "../../../types/CheckoutFormValuesProps";

const addressCountryFieldName =
  "community_member_billing_info_attributes.address_country";
const taxIdTypeFieldName =
  "community_member_billing_info_attributes.tax_id_type";
const taxIdTypeLabelFieldName =
  "community_member_billing_info_attributes.tax_id_type_label";
const taxIdValueFieldName =
  "community_member_billing_info_attributes.tax_id_value";
const fieldNames = {
  addressCountry: addressCountryFieldName,
  taxIdType: taxIdTypeFieldName,
  taxIdTypeLabel: taxIdTypeLabelFieldName,
  taxIdValue: taxIdValueFieldName,
};

export const CheckoutFormTaxIdForm = () => {
  const {
    watch,
    register,
    trigger,
    clearErrors,
    setValue,
    formState: { errors },
  } = useFormContext<CheckoutFormValuesProps>();
  const {
    community_member_billing_info_attributes: {
      address_country: selectedCountry,
    } = {},
  } = watch();

  const {
    isTaxCollectionEnabled,
    isTaxIdMandatory,
    isBrazilianAccountAndProcessorStripeBr: isBrazilianCommunity,
  } = usePaywallCheckoutContext();

  const config = useMemo<UseTaxIdFormConfig>(
    () => ({
      fieldNames,
      selectedCountry,
      isBrazilianCommunity,
      isTaxCollectionEnabled,
      isTaxIdMandatory,
    }),
    [
      selectedCountry,
      isBrazilianCommunity,
      isTaxCollectionEnabled,
      isTaxIdMandatory,
    ],
  );

  const {
    handleTaxIdTypeChange,
    isRequired,
    isTaxIdValueRequired,
    isVisible,
    selectedTaxIdType,
    selectOptions,
    taxIdTypeLabel,
  } = useTaxIdForm(config);

  if (!isVisible) {
    return null;
  }

  const pattern = selectedTaxIdType?.formOptions?.pattern || /.+/;

  const taxIdValueRegistration = register(taxIdValueFieldName, {
    required: isTaxIdValueRequired,
    pattern,
  });
  const taxIdValueError = get(errors, taxIdValueFieldName) as
    | FieldError
    | undefined;

  const handleTaxIdValueBlur = async (event: FocusEvent<HTMLInputElement>) => {
    await taxIdValueRegistration.onBlur(event);
    await trigger(taxIdValueFieldName);
  };

  // Bypass registration.onChange so no error can be set on keystroke;
  // clearErrors removes any error left over from a prior blur/submit
  // while the member is retyping.
  const handleTaxIdValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(taxIdValueFieldName, event.target.value, {
      shouldValidate: false,
      shouldDirty: true,
    });
    clearErrors(taxIdValueFieldName);
  };

  return (
    <CheckoutForm.Item name="tax_id" optional={!isRequired}>
      <Select
        variant="single"
        name="taxIdTypeLabel"
        data={selectOptions}
        value={taxIdTypeLabel}
        onChange={handleTaxIdTypeChange}
        filterBy={filterTaxIdTypeOptionBySearchValue}
        hideSelectedItems
        dataTestId={`checkout-form-${taxIdTypeFieldName}`}
      />
      {taxIdTypeLabel && (
        <TextInput
          rootClassName="mt-2"
          {...taxIdValueRegistration}
          onChange={handleTaxIdValueChange}
          onBlur={handleTaxIdValueBlur}
          invalid={!!taxIdValueError}
          error={
            taxIdValueError &&
            t(
              `paywall_checkout.form.errors.${taxIdValueFieldName}_${taxIdValueError.type}`,
            )
          }
          aria-label={t("paywall_checkout.form.fields.tax_id")}
          placeholder={selectedTaxIdType?.example}
          data-testid={`checkout-form-${taxIdValueFieldName}-input`}
        />
      )}
    </CheckoutForm.Item>
  );
};
